import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useTheme } from "../common/ThemeContext";
import { fetchAuth } from "../common/api";
import NiivueViewer from "../players/NiivueViewer";
import ModalViewer from "../players/ModalViewer";

const SectionCard = ({ title, right, children }) => {
  const { isDarkMode } = useTheme();
  return (
    <section
      className={`rounded-2xl p-6 shadow-lg border ${
        isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
};

SectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  right: PropTypes.node,
  children: PropTypes.node,
};

export default function ImageryTab({ playerId, previewEnabled = false }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const [images, setImages] = useState([]); // [{ id, type, name, url, isVolume }]
  const [selected, setSelected] = useState(null);
  const [modality, setModality] = useState("IRM");
  const [location, setLocation] = useState("");
  const [file, setFile] = useState(null);
  const prevRef = useRef([]);

  // API helpers
  const listImagingMeta = async (pid) => {
    const res = await fetchAuth(`/players/${pid}/imaging`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Error list_imaging");
    return data;
  };

  const saveImagingMeta = async (pid, { file, modality, localisation }) => {
    const form = new FormData();
    form.append("file", file);
    form.append("modality", modality);
    form.append("localisation", localisation);
    form.append("is_medical", "true");

    const res = await fetchAuth(`/players/${pid}/imaging`, {
      method: "POST",
      body: form,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Error add_imaging");
    return data;
  };

  const deleteImagingMeta = async (imagingId) => {
    const res = await fetchAuth(`/imaging/${imagingId}`, { method: "DELETE" });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || "Error delete_imaging");
    }
  };

  // Load images
  useEffect(() => {
    (async () => {
      try {
        const meta = await listImagingMeta(playerId);
        setImages(
          meta.map((m) => ({
            id: m.id,
            type: m.modality,
            name: m.name,
            url: m.url,
            isVolume: /\.(nii(\.gz)?|hdr(\.gz)?|img(\.gz)?|gii|nrrd|nhdr|mha|mhd|raw|zraw|mnc|dcm|dicom)$/i.test(m.name),
          }))
        );
      } catch (e) {
        
       
      }
    })();
  }, [playerId]);

  // Release blobs
  useEffect(() => {
    const removed = prevRef.current.filter(
      (prev) => !images.some((img) => img.url === prev.url)
    );
    removed.forEach((i) => URL.revokeObjectURL(i.url));
    prevRef.current = images;
  }, [images]);

  useEffect(() => () => {
    images.forEach((i) => URL.revokeObjectURL(i.url));
  }, []);

  // Callbacks
  const handleAddImage = async () => {
    if (!file || !location) return;
    const safeLoc = location.trim().replace(/\s+/g, "_").toLowerCase();
    const url = URL.createObjectURL(file);
    const isVolume = /\.(nii(\.gz)?|hdr(\.gz)?|img(\.gz)?|gii|nrrd|nhdr|mha|mhd|raw|zraw|mnc|dcm|dicom)$/i.test(file.name);

    const saved = await saveImagingMeta(playerId, {
      file,
      modality,
      localisation: safeLoc,
    });

    setImages((prev) => [
      ...prev,
      {
        id: saved.id,
        type: modality,
        name: saved.name || `${safeLoc}_${playerId}`,
        url: saved.url || url,
        isVolume,
      },
    ]);

    setFile(null);
    setLocation("");
  };

  const handleDeleteImage = async (img) => {
    if (!window.confirm(t("staff_update.delete_confirm", { defaultValue: "Delete '{{name}}'?", name: img.name }))) return;
    await deleteImagingMeta(img.id);
    if (img.url) URL.revokeObjectURL(img.url);
    setImages((prev) => prev.filter((i) => i.id !== img.id));
    if (selected?.id === img.id) setSelected(null);
  };

  const handleSelectImg = (img) => {
    if (!previewEnabled) return;
    setSelected(img);
  };

  const renderMedia = (img) => {
    const isNifti = /(\.nii(\.gz)?)$/i.test(img.name);
    if (isNifti && img.url) {
      return <NiivueViewer fileUrl={img.url} fileName={img.name} />;
    }
    return (
      <div className="w-full h-40 flex items-center justify-center bg-gray-800/60">
        <span className="text-sm text-gray-400">{t("staff_update.preview_not_available", { defaultValue: "Preview not available" })}</span>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <SectionCard title={t("staff_update.add_file", { defaultValue: "Add file" })}>
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block mb-1 font-medium">{t("staff_update.type_of_exam", { defaultValue: "Type of examination" })}</label>
            <select
              value={modality}
              onChange={(e) => setModality(e.target.value)}
              className={`w-full rounded-lg p-2 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                isDarkMode
                  ? "bg-gray-900 border-gray-700 text-gray-100"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              <option value="IRM">{t("staff_update.irm", { defaultValue: "MRI" })}</option>
              <option value="Echo">{t("staff_update.echography", { defaultValue: "Echography" })}</option>
            </select>
          </div>

          <div>
            <label className="block mb-1 font-medium">{t("staff_update.localisation", { defaultValue: "Localisation" })}</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={t("staff_update.localisation_placeholder", { defaultValue: "left knee, right ankle, etc." })}
              className={`w-full rounded-lg p-2 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                isDarkMode
                  ? "bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
              }`}
            />
          </div>

          <div>
            <label className="block mb-1 font-medium">{t("staff_update.files", { defaultValue: "Files" })}</label>
            <input
              type="file"
            accept=".dcm,.dicom,.dm,.nii,.nii.gz,.hdr,.hdr.gz,.img,.img.gz,.gii,.dscalar.nii,.dtseries.nii,.dlabel.nii,.pscalar.nii,.ptseries.nii,.pconn.nii,.nrrd,.nhdr,.mha,.mhd,.raw,.zraw,.mnc,.tif,.tiff,.jpg,.jpeg,.jp2,.j2k,.jpx,.png,.bmp,.gif,.avi,.mp4,.mpeg,.mpg,.mov,.wmv,.mkv,application/dicom"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full"
            />
          </div>
        </div>

        <button
          onClick={handleAddImage}
          className={`mt-6 px-6 py-2 rounded-xl font-semibold text-white shadow focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            isDarkMode
              ? "bg-indigo-600 hover:bg-indigo-700 focus:ring-offset-gray-900"
              : "bg-indigo-600 hover:bg-indigo-700 focus:ring-offset-white"
          }`}
          disabled={!file || !location}
        >
          {t("common.add")}
        </button>
      </SectionCard>

      <SectionCard title={t("staff_update.irm", { defaultValue: "IRM" })}>
        {images.filter((i) => i.type === "IRM").length ? (
          previewEnabled ? (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images
                .filter((i) => i.type === "IRM")
                .map((img) => (
                  <figure
                    key={img.id}
                    className="relative rounded-xl overflow-hidden shadow cursor-pointer group"
                    onClick={() => handleSelectImg(img)}
                  >
                    {renderMedia(img)}
                    <a
                      href={img.url}
                      download={img.name}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-gray-900/70 rounded-full p-1"
                      title={t("staff_update.download", { defaultValue: "Download" })}
                    >
                      ⬇
                    </a>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(img);
                      }}
                      className="absolute top-2 right-10 opacity-0 group-hover:opacity-100 bg-red-600/80 hover:bg-red-700 rounded-full p-1"
                      title={t("staff_update.delete", { defaultValue: "Delete" })}
                    >
                      🗑
                    </button>
                    <figcaption className="p-2 text-center text-sm truncate">
                      {img.name}
                    </figcaption>
                  </figure>
                ))}
            </div>
          ) : (
            <ul className="space-y-2">
              {images
                .filter((i) => i.type === "IRM")
                .map((img) => (
                  <li
                    key={img.id}
                    className={`flex items-center justify-between rounded-lg p-2 border ${
                      isDarkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-300 bg-white"
                    }`}
                  >
                    <span className="truncate">{img.name}</span>
                    <div className="space-x-2">
                      <a
                        href={img.url}
                        download={img.name}
                        className={`${isDarkMode ? "text-indigo-400" : "text-indigo-600"} hover:underline`}
                      >
                        ⬇ {t("staff_update.download", { defaultValue: "Download" })}
                      </a>
                      <button
                        onClick={() => handleDeleteImage(img)}
                        className="text-red-600 hover:text-red-700"
                        title={t("staff_update.delete", { defaultValue: "Delete" })}
                      >
                        🗑
                      </button>
                    </div>
                  </li>
                ))}
            </ul>
          )
        ) : (
          <p className="text-gray-500">{t("staff_update.no_irm_files", { defaultValue: "No IRM files added." })}</p>
        )}
      </SectionCard>

      <SectionCard title={t("staff_update.echography", { defaultValue: "Echography" })}>
        {images.filter((i) => i.type === "Echo").length ? (
          previewEnabled ? (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images
                .filter((i) => i.type === "Echo")
                .map((img) => (
                  <figure
                    key={img.id}
                    className="relative rounded-xl overflow-hidden shadow cursor-pointer group"
                    onClick={() => handleSelectImg(img)}
                  >
                    {renderMedia(img)}
                    <a
                      href={img.url}
                      download={img.name}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-gray-900/70 rounded-full p-1"
                      title={t("staff_update.download", { defaultValue: "Download" })}
                    >
                      ⬇
                    </a>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(img);
                      }}
                      className="absolute top-2 right-10 opacity-0 group-hover:opacity-100 bg-red-600/80 hover:bg-red-700 rounded-full p-1"
                      title={t("staff_update.delete", { defaultValue: "Delete" })}
                    >
                      🗑
                    </button>
                    <figcaption className="p-2 text-center text-sm truncate">
                      {img.name}
                    </figcaption>
                  </figure>
                ))}
            </div>
          ) : (
            <ul className="space-y-2">
              {images
                .filter((i) => i.type === "Echo")
                .map((img) => (
                  <li
                    key={img.id}
                    className={`flex items-center justify-between rounded-lg p-2 border ${
                      isDarkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-300 bg-white"
                    }`}
                  >
                    <span className="truncate">{img.name}</span>
                    <div className="space-x-2">
                      <a
                        href={img.url}
                        download={img.name}
                        className={`${isDarkMode ? "text-indigo-400" : "text-indigo-600"} hover:underline`}
                      >
                        ⬇ {t("staff_update.download", { defaultValue: "Download" })}
                      </a>
                      <button
                        onClick={() => handleDeleteImage(img)}
                        className="text-red-600 hover:text-red-700"
                        title={t("staff_update.delete", { defaultValue: "Delete" })}
                      >
                        🗑
                      </button>
                    </div>
                  </li>
                ))}
            </ul>
          )
        ) : (
          <p className="text-gray-500">{t("staff_update.no_echo_files", { defaultValue: "No echo files added." })}</p>
        )}
      </SectionCard>

      {previewEnabled && selected && (
        <ModalViewer img={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

ImageryTab.propTypes = {
  playerId: PropTypes.string.isRequired,
  previewEnabled: PropTypes.bool,
};
